import React, { useEffect, useState, useContext } from "react";
import { Form, Spinner, Alert, Container, Card, Button } from "react-bootstrap";
import {
  InputGroup,
  InputGroupText,
  Input,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { useNavigate } from "react-router-dom";
import HusariaHeader from "views/pages/components/HusariaHeader";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import paginationFactory from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";

const Voicemail = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const title = "Voicemails";
  const parentName = "HUCS";
  const rowKey = "voicemail_uuid";
  const navigate = useNavigate();

  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);

  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
  const canDelete = hasPermission(userData, PERMISSIONS.DELETE);

  const canPerformActions =
    hasPermission(userData, PERMISSIONS.CREATE) ||
    hasPermission(userData, PERMISSIONS.EDIT) ||
    hasPermission(userData, PERMISSIONS.DELETE);

  const visibleColumns = [
    { key: "voicemail_id", label: "Voicemail ID" },
    { key: "voicemail_mail_to", label: "Mail To" },
    { key: "voicemail_attach_file", label: "Attached" },
    { key: "voicemail_local_after_email", label: "Keep Local After Email" },
    { key: "voicemail_enabled", label: "Enabled" },
    { key: "voicemail_description", label: "Description" },
  ];

  const add = "/admin/hucs/add/voicemail";
  const detail = "/admin/hucs/view-voicemail/";
  const greeting = "/admin/hucs/view-greetings/";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearchTerm] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedVoicemail, setSelectedVoicemail] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const response = await fetch(`${serverHost}/vm_ext_list/${site.site_uuid}`);
      if (!response.ok) throw new Error("Failed to fetch voicemails");

      const data = await response.json();
      setRows(data.result || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToMessages = (id) => {
    navigate(detail + id);
  };

  const handleToGreetings = (id) => {
    navigate(greeting + id);
  };

  const openDeleteModal = (row) => {
    setSelectedVoicemail(row);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
    setSelectedVoicemail(null);
  };

  const handleDelete = async () => {
    if (!selectedVoicemail?.voicemail_uuid) return;

    try {
      setDeleteLoading(true);
      const response = await fetch(
        `${serverHost}/delete_voicemail/${selectedVoicemail.voicemail_uuid}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete voicemail");
      }

      closeDeleteModal();
      fetchRows();
    } catch (err) {
      console.error(err);
      alert("Error deleting voicemail");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (index === 0) {
        return canEdit ? (
          <a
            style={{
              color: isDark ? "#60a5fa" : "#0d6efd",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            {cell ?? "-"}
          </a>
        ) : (
          <span style={{ fontWeight: 600 }}>{cell ?? "-"}</span>
        );
      }

      return cell ?? "-";
    },
  }));

  if (canPerformActions) {
    columns.push({
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="default"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleToGreetings(row.voicemail_uuid);
            }}
          >
            Greetings ({row.greetings_count || 0})
          </Button>

          <Button
            variant="success"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleToMessages(row.voicemail_uuid);
            }}
          >
            Messages ({row.messages_count || 0})
          </Button>

          {canDelete && (
            <Button
              variant="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openDeleteModal(row);
              }}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    });
  }

  const rowEvents = {
    onClick: (e, row) => {
      navigate(`/admin/hucs/edit/voicemail/${row[rowKey]}`);
    },
  };

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const filteredRows = React.useMemo(() => {
    if (!search) return rows;

    return rows.filter((row) =>
      visibleColumns.some((col) =>
        String(row[col.key] ?? "")
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    );
  }, [rows, search, visibleColumns]);

  useEffect(() => {
    fetchRows();
  }, []);

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading voicemails...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader name={title} parentName={parentName} padding="4.5rem" />

      <Container fluid className="mt--6">
        <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
          >
            <div className="d-flex align-items-center">
              <Form className="navbar-search navbar-search-light form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge search-border">
                    <InputGroupText>
                      <i className="fas fa-search search-icon" />
                    </InputGroupText>
                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>

            <div className="d-flex gap-2">
              <Button variant="info" size="sm" onClick={fetchRows}>
                Refresh
              </Button>
              <Button size="sm" onClick={() => navigate(add)}>
                + Add Voicemail
              </Button>
            </div>
          </Card.Header>

          <Card.Body style={{ padding: 0 }}>
            <div style={{ overflowX: "auto" }}>
              <BootstrapTable
                keyField={rowKey}
                data={filteredRows}
                columns={columns}
                bootstrap4
                pagination={pagination}
                bordered={false}
                hover
                rowEvents={canEdit ? rowEvents : {}}
                rowStyle={{ cursor: canEdit ? "pointer" : "default" }}
                classes="align-items-center table-flush"
                noDataIndication={() =>
                  loading ? (
                    <div className="text-center">Loading...</div>
                  ) : (
                    <div className="text-center">No records found.</div>
                  )
                }
              />
            </div>
          </Card.Body>
        </Card>
      </Container>

      <Modal isOpen={deleteModalOpen} toggle={closeDeleteModal} centered>
        <ModalHeader
          toggle={closeDeleteModal}
          className={`text-danger ${isDark ? "bg-dark text-white" : ""}`}
        >
          Delete Voicemail
        </ModalHeader>

        <ModalBody className="card-body">
          Are you sure you want to delete{" "}
          <strong>{selectedVoicemail?.voicemail_id || "this voicemail"}</strong>?
          <br />
          <strong>This action cannot be undone.</strong>
        </ModalBody>

        <ModalFooter className="card-body">
          <Button variant="light" onClick={closeDeleteModal} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Voicemail;